<script setup lang="ts">
import type { components } from "~/api_types";

type ModeResponse = components["schemas"]["ModeResponse"];

const props = withDefaults(
  defineProps<{
    mode: ModeResponse;
    // For use inside an already-colored badge: draw the bare mask in `currentColor`
    // instead of the official white-letter-on-color pictogram.
    monochrome?: boolean;
    size?: number;
  }>(),
  { monochrome: false, size: 18 }
);

// Official Munich transit pictograms. The `path`s are copied verbatim from the map
// sprites in `map/martin/sprites/navigatum/{u-bahn,s-bahn,bus,tram}-munich.svg` -
// single-path "shape-minus-letter" masks on a 15×15 canvas. The colors match the
// `icon-color`s used for the same sprites in `map/martin/styles/navigatum-basemap.json`,
// so the detail panel and the map stay in sync.
interface Pictogram {
  shape: "square" | "circle";
  color: string;
  path: string;
  evenOdd?: boolean;
}
const PICTOGRAMS: Partial<Record<ModeResponse, Pictogram>> = {
  subway: {
    shape: "square",
    color: "#086ab1",
    path: "M0 0v15h15V0Zm2.954 1.812H5.62v7.056c0 1.287.45 2.206 1.872 2.206 1.395 0 1.855-.92 1.855-2.206V1.812h2.7v7.317c0 3.06-2.512 4.186-4.564 4.186-2.06 0-4.528-1.126-4.528-4.186z",
  },
  suburban: {
    shape: "circle",
    color: "#00894d",
    evenOdd: true,
    path: "M7.5 0C3.772 0 0 3.772 0 7.5S3.772 15 7.5 15 15 11.228 15 7.5 11.228 0 7.5 0Zm-.198 2.217c1.53 0 2.889.495 3.978 1.341v1.773a5.589 5.589 0 0 0-4.014-1.908c-.936 0-1.476.495-1.476 1.08 0 2.475 6.138.738 6.138 4.788 0 1.638-1.584 3.546-4.257 3.546a6.813 6.813 0 0 1-4.149-1.512V9.201a5.31 5.31 0 0 0 4.14 2.268c1.008 0 1.818-.576 1.818-1.188 0-2.304-6.048-.882-6.048-4.86 0-2.079 2.043-3.204 3.87-3.204z",
  },
  bus: {
    shape: "circle",
    color: "#085d6f",
    path: "M7.5 0C3.77-.003.003 3.77 0 7.5c-.002 3.731 3.76 7.498 7.49 7.5 3.73.002 7.508-3.76 7.51-7.49C15.003 3.78 11.23.003 7.5 0Zm4.876 4.828c.19 0 .36.009.54.036l.54.108-.189.756a2.304 2.304 0 0 0-1.188-.135.657.657 0 0 0-.477.324.855.855 0 0 0-.063.36c0 .18.054.333.144.44.108.109.234.19.387.27.144.064.306.136.495.199.18.072.342.153.504.26.36.256.563.677.54 1.117.012.475-.206.927-.585 1.215-.18.135-.378.234-.612.288-.225.063-.45.09-.71.09l-.577-.018a2.529 2.529 0 0 1-.558-.126l.018-.783c.144.063.306.117.468.144.18.027.333.045.495.045.249-.001.495-.057.72-.162a.72.72 0 0 0 .225-.243.846.846 0 0 0 .09-.396.668.668 0 0 0-.153-.441.927.927 0 0 0-.36-.261 8.883 8.883 0 0 0-.486-.198 2.357 2.357 0 0 1-.495-.261 1.371 1.371 0 0 1-.369-.414 1.53 1.53 0 0 1-.153-.72c0-.252.054-.486.153-.675.09-.18.225-.333.405-.45.162-.126.36-.225.576-.27a2.45 2.45 0 0 1 .675-.1zm-10.737.09h1.665c.216 0 .405.027.594.072.18.036.351.126.486.216s.252.225.333.387c.081.162.126.369.126.594 0 .315-.09.54-.26.729a1.82 1.82 0 0 1-.676.432c.153.027.297.072.432.135.135.072.252.153.351.243.09.099.18.216.243.36.116.356.1.743-.045 1.089a1.555 1.555 0 0 1-.333.486 1.62 1.62 0 0 1-1.152.414H1.64zm4.311 0h.972v3.23c0 .163.01.307.036.46.027.144.072.279.135.387a.776.776 0 0 0 .27.27.81.81 0 0 0 .441.099c.171 0 .324-.027.423-.09a.855.855 0 0 0 .27-.27c.072-.117.108-.252.135-.396.027-.153.045-.315.045-.46v-3.23h.972V8.22c-.018.612-.18 1.098-.486 1.44-.306.333-.774.504-1.377.504-.62 0-1.089-.171-1.386-.513-.297-.333-.45-.82-.45-1.431zm-3.33.747v1.404h.468a.81.81 0 0 0 .558-.19.765.765 0 0 0 .207-.566c0-.216-.045-.378-.17-.486a.756.756 0 0 0-.514-.162zm0 2.115v1.557h.63c.252 0 .45-.081.585-.234a.882.882 0 0 0 .18-.576.72.72 0 0 0-.198-.54.72.72 0 0 0-.54-.207z",
  },
  tram: {
    shape: "square",
    color: "#d92727",
    path: "M0 0v15h15V0Zm1.227 5.232h2.754v.693h-.963v3.78h-.837v-3.78h-.963Zm4.5 1.107.126.009.117.027-.198.72c-.081-.027-.153-.027-.216-.027a.54.54 0 0 0-.279.072c-.072.054-.144.099-.18.18-.045.09-.072.18-.09.27a1.784 1.784 0 0 0-.027.315v1.8h-.81V6.411h.72l.045.423c.108-.171.216-.288.342-.369a.72.72 0 0 1 .45-.126Zm1.854 0c.387 0 .675.081.864.261.189.162.279.387.279.684v1.809c0 .108 0 .198.018.306l.045.315H8.04v-.009l-.054-.396a.936.936 0 0 1-.333.342.926.926 0 0 1-.486.117c-.324 0-.549-.081-.72-.252-.153-.18-.234-.423-.234-.729a.9.9 0 0 1 .09-.45c.063-.135.144-.243.252-.342.108-.09.252-.162.396-.198.263-.074.54-.092.81-.054l.216.027v-.36c0-.18-.054-.306-.144-.387a.603.603 0 0 0-.414-.117c-.27.008-.536.069-.783.18l-.135-.558c.162-.081.342-.126.531-.153.189-.027.378-.036.54-.036Zm5.274 0c.153 0 .288.027.396.081a.806.806 0 0 1 .279.216c.151.213.23.468.225.729v2.322h-.774V7.446c0-.153-.027-.27-.09-.36-.072-.108-.18-.153-.324-.153a.315.315 0 0 0-.216.072.576.576 0 0 0-.234.387l-.027.243v2.079h-.783V7.446a.63.63 0 0 0-.09-.378.405.405 0 0 0-.333-.153.394.394 0 0 0-.225.072.639.639 0 0 0-.234.405l-.027.243v2.07h-.792V6.411h.702l.054.441a.855.855 0 0 1 .828-.495c.135 0 .243.009.333.036.09.027.18.063.225.117a.422.422 0 0 1 .162.171.6.6 0 0 1 .108.171.891.891 0 0 1 .297-.36.913.913 0 0 1 .54-.153zm-5.31 1.863c-.189 0-.324.054-.405.162a.648.648 0 0 0-.045.729c.063.09.18.135.315.135a.568.568 0 0 0 .387-.153.45.45 0 0 0 .162-.36v-.459h.018l-.18-.036a1.386 1.386 0 0 0-.252-.018Z",
  },
};

const pictogram = computed(() => PICTOGRAMS[props.mode]);
</script>

<template>
  <svg
    v-if="pictogram"
    :width="props.size"
    :height="props.size"
    viewBox="0 0 15 15"
    class="inline-block"
    aria-hidden="true"
    focusable="false"
  >
    <template v-if="monochrome">
      <path :d="pictogram.path" :fill-rule="pictogram.evenOdd ? 'evenodd' : undefined" fill="currentColor" />
    </template>
    <template v-else>
      <!-- White backing keeps the knocked-out letter white in both light and dark mode;
           the mask's outer edge coincides with the backing, so nothing leaks. -->
      <rect v-if="pictogram.shape === 'square'" width="15" height="15" fill="#fff" />
      <circle v-else cx="7.5" cy="7.5" r="7.5" fill="#fff" />
      <path :d="pictogram.path" :fill-rule="pictogram.evenOdd ? 'evenodd' : undefined" :fill="pictogram.color" />
    </template>
  </svg>
</template>
